import React, { useState, useEffect, Fragment } from "react";
import { Col, Row, Typography, Tooltip } from "antd";
import { DateField, InputField, ReactSelectField } from "../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../atoms/FormElement/InputNumberField";
import { useWatch } from "react-hook-form";
import { additional_amount_type, amount_type, payment_modes } from "./utils";
import { DeleteIcon } from "../../../../assets/svg";
import dayjs from "dayjs";
import { CheckOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { convertToDayjs, formatDateByType } from "../../../libs/utils/date/formatDate";
import { useSelector } from "react-redux";
import { DATE_FORMATS } from "../../../libs/utils";

export default (props) => {
  const disableTypeModeDueDate = props.disableTypeModeDueDate === true;

  const {
    control,
    errors,
    billingType,
    remove,
    setValue,
    getValues,
    enableClone,
    fields,
    totalAnnualRent,
    depositPayments,
    bookingPayments,
    dueData,
    unitArea,
    register,
    onDeleteBookingPayment,
    onUpdateBookingPayment,
    resetBookingPaymentEditTrigger,
  } = props;

  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const [enable, setEnable] = useState(true);
  const [disable, setDisable] = useState(false);
  const [typeM, setTypeM] = useState("");
  const [editableBookingPaymentIndex, setEditableBookingPaymentIndex] = useState<number | null>(null);

  const configData = { rental_commission_percentage: 0.05 };
  const { Text, Paragraph } = Typography;

  const othercharges = useWatch({ control, name: "othercharges" });
  const watchedBookingPayments = useWatch({ control, name: "bookingPayments" });

  const onChange = (e, index) => {
    if (e.value == "rental_commission") {
      setValue(`othercharges[${index}].intallments`, { label: "1", value: 1 });
      setValue(`othercharges[${index}].amount`, getValues("annualBaseRent") * configData?.rental_commission_percentage);
    } else {
      setValue(`othercharges[${index}].amount`, 0);
      setValue(
        `othercharges[${index}].vat`,
        billingType?.find((paymentType) => paymentType?.value === e?.value)?.vatInPercentage ?? 0
      );
    }
    e.value == "PARKING" ? setEnable(true) : setEnable(false);
  };

  const isTurnoverRentOnly = bookingDetails?.torType === "turnoverRentOnly";
  const amountTypeOptions = additional_amount_type(unitArea, { disablePercentage: isTurnoverRentOnly });

  const getDefaultAmountTypeOption = () => {
    const preferred = amountTypeOptions.find((type) => type?.default && !type?.isDisabled);
    return preferred || amountTypeOptions.find((type) => !type?.isDisabled) || amountTypeOptions[0];
  };

  const onAmountChange = (e, index) => {
    let total: any = 0;
    const rent = totalAnnualRent || null; //TODO: ANNUALRENT FINAL AND ADD HERE
    const type = getValues(`othercharges[${index}].amountType`)?.value;
    const amount = parseFloat(getValues(`othercharges[${index}].actualAmount`) || 0);
    const vatPercent = parseFloat(getValues(`othercharges[${index}].vat`) || 0);
    let areaSquerFeet = parseFloat(unitArea);

    if (type) {
      switch (type) {
        case "percentage":
          setValue(`othercharges[${index}].total`, "Varies by plan");
          return;
        case "fixed":
        case "amount":
          let fixVat = vatPercent * 0.01 * amount; // + rent : removed for bug: 8056
          total = amount + fixVat; //  + rent : removed for bug: 8056
          break;
        case "areaSqft":
          let totalAmount = amount * areaSquerFeet;
          let totalVAt = vatPercent * 0.01 * totalAmount; // for story 13685 sprint 61
          total = totalAmount + totalVAt; // for story 13685 sprint 61
          break;
        default:
          break;
      }
    } else setValue(`othercharges[${index}].amountType`, getDefaultAmountTypeOption());

    setValue(
      `othercharges[${index}].total`,
      type == "percentage" || `othercharges[${index}].amountType` == "percentage" ? "Varies by plan" : total
    );
  };

  useEffect(() => {
    othercharges?.map((e, i) => onAmountChange(null, i));
  }, [totalAnnualRent]);

  useEffect(() => {
    othercharges?.forEach((charge, index) => {
      const amountType = charge?.amountType?.value || charge?.amountType;
      const total = getValues(`othercharges[${index}].total`);
      if (amountType === "percentage" && total !== "Varies by plan") {
        setValue(`othercharges[${index}].total`, "Varies by plan");
      }
    });
  }, [othercharges, getValues, setValue]);

  useEffect(() => {
    if (editableBookingPaymentIndex !== null && editableBookingPaymentIndex >= (bookingPayments?.length || 0)) {
      setEditableBookingPaymentIndex(null);
    }
  }, [bookingPayments, editableBookingPaymentIndex]);

  useEffect(() => {
    setEditableBookingPaymentIndex(null);
  }, [resetBookingPaymentEditTrigger]);

  useEffect(() => {
    bookingPayments?.forEach((payment, index) => {
      const watchedPayment = watchedBookingPayments?.[index];
      const amountType =
        watchedPayment?.amountType?.value || watchedPayment?.amountType || payment?.amountType?.value || payment?.amountType || payment?.paymentType;
      const totalAmount = watchedPayment?.totalAmount ?? payment?.totalAmount;

      if (amountType === "percentage" && totalAmount !== "Varies by plan") {
        setValue(`bookingPayments.${index}.totalAmount`, "Varies by plan");
      }
    });
  }, [bookingPayments, watchedBookingPayments, setValue]);

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const calculateBookingPaymentTotal = (payment) => {
    const amountType = payment?.amountType?.toLowerCase?.() || payment?.amountType;
    const actualAmount = parseNumber(payment?.actualAmount);
    const vat = parseNumber(payment?.vat);

    if (amountType === "percentage") {
      const rentAmount = actualAmount * 0.01 * parseNumber(totalAnnualRent);
      const vatAmount = vat * 0.01 * rentAmount;
      return Number((rentAmount + vatAmount).toFixed(2));
    }

    const vatAmount = vat * 0.01 * actualAmount;
    return Number((actualAmount + vatAmount).toFixed(2));
  };

  const onBookingPaymentChange = (index, payload) => {
    const currentPayment = bookingPayments?.[index] || {};
    const updatedPayment = { ...currentPayment, ...payload };
    const amountTypeValue = updatedPayment?.amountType?.value || updatedPayment?.amountType;

    if ("amountType" in payload || "actualAmount" in payload || "vat" in payload) {
      if (amountTypeValue === "percentage") {
        updatedPayment.totalAmount = "Varies by plan";
        setValue(`bookingPayments.${index}.totalAmount`, "Varies by plan");
      } else {
        const totalAmount = calculateBookingPaymentTotal(updatedPayment);
        updatedPayment.totalAmount = totalAmount;
        setValue(`bookingPayments.${index}.totalAmount`, totalAmount);
      }
    }

    onUpdateBookingPayment?.(index, updatedPayment);
  };

  const rowActionIconStyle = { fontSize: 16, marginTop: 6, color: "#1f4462" };

  return (
    <>
      <Row gutter={[24, 24]} style={{ letterSpacing: "1.2px" }}>
        <Col span={24} className="ft-divider" />
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-1">TYPE</Text>
        </Col>
        <Col span={3}>
          <Text className="fontWeight600 ag-default mb-2">MODE</Text>
        </Col>
        <Col span={3}>
          <Text className="fontWeight600 ag-default mb-2">DUE DATE</Text>
        </Col>
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-2">AMOUNT TYPE</Text>
        </Col>
        <Col span={3}>
          <Text className="fontWeight600 ag-default mb-2">AMOUNT/%</Text>
        </Col>
        <Col span={2}>
          <Text className="fontWeight600 ag-default mb-2">TAX %</Text>
        </Col>
        <Col span={3}>
          <Text className="fontWeight600 ag-default mb-2">TOTAL AMOUNT</Text>
        </Col>
        {/* <Col span={24} className="ft-divider" /> */}
        <Col span={24} style={{ height: "16px" }} />
      </Row>

      {bookingPayments &&
        bookingPayments?.length > 0 &&
        bookingPayments.map((item, index) => {
          const watchedAmountType = watchedBookingPayments?.[index]?.amountType;
          const bookingAmountType = watchedAmountType?.value || item?.amountType || item?.paymentType;
          const isBookingPaymentEditable = editableBookingPaymentIndex === index;

          return (
          <Row gutter={24} key={item?.__rowId || index}>
            <Col span={4}>
              <ReactSelectField
                isRequired={false}
                isDisabled={props.disableAll ? true : disableTypeModeDueDate || !isBookingPaymentEditable}
                fieldname={`bookingPayments.${index}.typeActualAmount`}
                iProps={{
                  placeholder: "Select",
                  isDisabled: props.disableAll ? true : disableTypeModeDueDate || !isBookingPaymentEditable,
                }}
                control={control}
                valueGot={billingType?.find((type) => type?.value === item?.type)}
                setValue={setValue}
                selectOption={billingType}
                onChange={(e) =>
                  onBookingPaymentChange(index, {
                    type: e?.value,
                    typeActualAmount: e,
                  })
                }
              />
            </Col>

            <Col span={3}>
              <ReactSelectField
                isRequired={false}
                isDisabled={props.disableAll ? true : disableTypeModeDueDate || !isBookingPaymentEditable}
                fieldname={`bookingPayments.${index}.modeOfPayment`}
                iProps={{
                  placeholder: "Select",
                  isDisabled: props.disableAll ? true : disableTypeModeDueDate || !isBookingPaymentEditable,
                }}
                control={control}
                initValue={payment_modes.find((mode) => mode?.value === item?.modeOfPayment)}
                selectOption={payment_modes}
                onChange={(e) => onBookingPaymentChange(index, { modeOfPayment: e?.value })}
              />
            </Col>

            <Col span={3}>
              <DateField
                isRequired={false}
                disabled={props.disableAll ? true : disableTypeModeDueDate || !isBookingPaymentEditable}
                fieldname={`bookingPayments.${index}.dueDate`}
                iProps={{ placeholder: DATE_FORMATS.display, format: DATE_FORMATS.display }}
                control={control}
                initValue={convertToDayjs(item?.dueDate || dueData)}
                onChange={(e) => onBookingPaymentChange(index, { dueDate: formatDateByType(e, "api") })}
              />
            </Col>

            <Col span={4}>
              <ReactSelectField
                isRequired={false}
                isDisabled={props.disableAll ? true : !isBookingPaymentEditable}
                fieldname={`bookingPayments.${index}.amountType`}
                iProps={{ placeholder: "Amount Type", isDisabled: props.disableAll ? true : !isBookingPaymentEditable }}
                control={control}
                initValue={amountTypeOptions?.find((type) => type?.value === bookingAmountType)}
                selectOption={amountTypeOptions}
                onChange={(e) => onBookingPaymentChange(index, { amountType: e?.value })}
              />
            </Col>

            <Col span={3}>
              <InputNumberField
                isRequired={false}
                disabled={props.disableAll ? true : !isBookingPaymentEditable}
                control={control}
                fieldname={`bookingPayments.${index}.actualAmount`}
                inputNumberProps={{ placeholder: "Amount" }}
                defaultValue={item?.actualAmount}
                format="currency"
                onChange={(value) => onBookingPaymentChange(index, { actualAmount: parseNumber(value) })}
              />
            </Col>

            <Col span={2}>
              <InputNumberField
                control={control}
                fieldname={`bookingPayments.${index}.vat`}
                disabled={props.disableAll ? true : !isBookingPaymentEditable}
                inputNumberProps={{
                  placeholder: "Tax",
                  min: 0,
                  max: 100,
                  precision: 2,
                }}
                rules={{ min: { value: 0, message: "VAT must be at least 0" }, max: { value: 100, message: "VAT cannot exceed 100" } }}
                defaultValue={item?.vat}
                onChange={(value) => onBookingPaymentChange(index, { vat: parseNumber(value) })}
              />
            </Col>

            <Col span={3}>
              {bookingAmountType === "percentage" ? (
                <InputField
                  isRequired={false}
                  disabled
                  fieldname={`bookingPayments.${index}.totalAmount`}
                  iProps={{ placeholder: "Varies by plan" }}
                  control={control}
                  initValue={"Varies by plan"}
                />
              ) : (
                <InputNumberField
                  isRequired={false}
                  control={control}
                  fieldname={`bookingPayments.${index}.totalAmount`}
                  disabled={true}
                  inputNumberProps={{ placeholder: "Total Amount" }}
                  defaultValue={item?.total}
                  format="currency"
                />
              )}
            </Col>

            <div style={{ display: "flex", marginTop: "6px" }}>
              {bookingAmountType === "percentage" && (
                <div style={{ marginTop: "3px" }}>
                  <Tooltip
                    placement="topLeft"
                    title={"Amount will be calculated based on the payment plan selected during the booking process"}
                    overlayClassName="tooltip-protected"
                  >
                    <span style={{ cursor: "pointer" }}>
                      <InfoCircleOutlined
                        style={{
                          background: "#108ee9",
                          color: "white",
                          fontSize: "20px",
                          borderRadius: "50%",
                        }}
                      />
                    </span>
                  </Tooltip>
                </div>
              )}

              {!props.disableAll && (
                <div style={{ marginLeft: "4px" }}>
                  <Tooltip
                    placement="topLeft"
                    overlayClassName="tooltip-protected"
                    title={isBookingPaymentEditable ? "Done" : "Edit"}
                  >
                    <span
                      onClick={() =>
                        setEditableBookingPaymentIndex((current) => (current === index ? null : index))
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {isBookingPaymentEditable ? (
                        <CheckOutlined style={rowActionIconStyle} />
                      ) : (
                        <EditOutlined style={rowActionIconStyle} />
                      )}
                    </span>
                  </Tooltip>
                </div>
              )}

              {!props.disableAll && (
                <div style={{ marginLeft: "4px" }}>
                  <Tooltip placement="topLeft" overlayClassName="tooltip-protected" title={"Delete"}>
                    <span
                      onClick={() => {
                        if (editableBookingPaymentIndex === index) setEditableBookingPaymentIndex(null);
                        onDeleteBookingPayment?.(index);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <DeleteIcon />
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          </Row>
          );
        })}

      {/* next others fields for addtiontional payments */}
      {fields.map((item, index) => {
        let isPercentage = othercharges[index]?.amountType?.value === "percentage" || item?.amountType === "percentage";
        return (
          <Fragment key={item.id}>
            <Row gutter={24} style={{ position: "relative" }}>
              <Col span={4}>
                <InputField
                  classes="d-none"
                  label="id"
                  fieldname={`othercharges[${index}].itemId`}
                  iProps={{ type: "number" }}
                  control={control}
                  initValue={item?.itemId || null}
                />

                <ReactSelectField
                  isRequired={true}
                  fieldname={`othercharges[${index}].billingChargesType`}
                  control={control}
                  iProps={{
                    placeholder: "Select",
                    isDisabled: props.disableAll ? true : disable,
                  }}
                  initValue={item?.billingChargesType}
                  selectOption={billingType}
                  onChange={(e) => onChange(e, index)}
                  rules={{ required: "Please select type" }}
                  validate={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.billingChargesType &&
                    "error"
                  }
                  validMessage={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.billingChargesType &&
                    errors?.othercharges[index]?.billingChargesType?.message
                  }
                />
              </Col>
              <Col span={3}>
                <ReactSelectField
                  isRequired={true}
                  fieldname={`othercharges[${index}].modeOfPayment`}
                  control={control}
                  iProps={{
                    placeholder: "Select",
                    isDisabled: props.disableAll ? true : false,
                  }}
                  selectOption={payment_modes}
                  rules={{ required: "Please select mode" }}
                  validate={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.modeOfPayment &&
                    "error"
                  }
                  validMessage={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.modeOfPayment &&
                    errors?.othercharges[index]?.modeOfPayment?.message
                  }
                />
              </Col>
              <Col span={3}>
                <DateField
                  label=""
                  fieldname={`othercharges[${index}].dueDate`}
                  control={control}
                  iProps={{
                    placeholder: DATE_FORMATS.display,
                    format: DATE_FORMATS.display,
                    disabledDate: (current) =>
                      current && dayjs(current).format("YYYY-MM-DD") < dayjs().format("YYYY-MM-DD"),
                  }}
                  initValue={item.dueDate}
                  setValue={setValue}
                  rules={{ required: "Please select date" }}
                  validate={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.dueDate &&
                    "error"
                  }
                  validMessage={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.dueDate &&
                    errors?.othercharges[index]?.dueDate?.message
                  }
                />
              </Col>
              <Col span={4}>
                <ReactSelectField
                  isRequired={true}
                  label={""}
                  fieldname={`othercharges[${index}].amountType`}
                  control={control}
                  iProps={{
                    placeholder: "Select",
                    isDisabled: props.disableAll ? true : disable,
                  }}
                  initValue={item?.amountType}
                  selectOption={amountTypeOptions}
                  onChange={(e) => {
                    onAmountChange(e, index);
                  }}
                  rules={{ required: "Please select amount type" }}
                  validate={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.amountType &&
                    "error"
                  }
                  validMessage={
                    Object.entries(errors).length > 0 &&
                    errors?.othercharges?.length > 0 &&
                    errors?.othercharges[index]?.amountType &&
                    errors?.othercharges[index]?.amountType?.message
                  }
                />
              </Col>
              <Col span={3}>
                <InputNumberField
                  isRequired={true}
                  fieldname={`othercharges[${index}].actualAmount`}
                  disabled={props.disableAll ? true : false}
                  inputNumberProps={{
                    placeholder: "Amount",
                  }}
                  onChange={() => onAmountChange(null, index)}
                  control={control}
                  defaultValue={item?.actualAmount}
                  rules={{
                    required: "Please enter amount",
                  }}
                  format="currency"
                />
              </Col>
              <Col span={2}>
                <InputNumberField
                  isRequired={true}
                  fieldname={`othercharges[${index}].vat`}
                  disabled
                  inputNumberProps={{
                    placeholder: "Tax",
                    min: 0,
                    max: 100,
                    precision: 2,
                  }}
                  onChange={() => onAmountChange(null, index)}
                  control={control}
                  defaultValue={item?.vat}
                  rules={{
                    required: "Please enter amount",
                    min: { value: 0, message: "VAT must be at least 0" },
                    max: { value: 100, message: "VAT cannot exceed 100" },
                  }}
                />
              </Col>
              <Col span={3}>
                <div>
                  {isPercentage ? (
                    <InputField
                      fieldname={`othercharges[${index}].total`}
                      iProps={{
                        placeholder: "Varies by plan",
                      }}
                      disabled
                      control={control}
                      initValue={"Varies by plan"}
                      validate={
                        Object.entries(errors).length > 0 &&
                        errors?.othercharges?.length > 0 &&
                        errors?.othercharges[index]?.total &&
                        "error"
                      }
                      validMessage={
                        Object.entries(errors).length > 0 &&
                        errors?.othercharges?.length > 0 &&
                        errors?.othercharges[index]?.total &&
                        errors?.othercharges[index]?.total?.message
                      }
                    />
                  ) : (
                    <InputNumberField
                      isRequired
                      fieldname={`othercharges[${index}].total`}
                      inputNumberProps={{ placeholder: "Total Amount" }}
                      control={control}
                      defaultValue={item?.total}
                      rules={{
                        required: "Please enter amount",
                        min: { value: 0, message: "Please enter correct amount" },
                      }}
                      format="currency"
                    />
                  )}
                </div>
              </Col>

              <div style={{ display: "flex", marginTop: "6px" }}>
                {othercharges[index]?.amountType?.value == "percentage" && (
                  <div style={{ marginTop: "3px" }}>
                    <Tooltip
                      placement="topLeft"
                      title={"Amount will be calculated based on the payment plan selected during the booking process"}
                      overlayClassName="tooltip-protected"
                    >
                      <span style={{ cursor: "pointer" }}>
                        <InfoCircleOutlined
                          style={{
                            background: "#108ee9",
                            color: "white",
                            fontSize: "20px",
                            borderRadius: "50%",
                          }}
                        />
                      </span>
                    </Tooltip>
                  </div>
                )}

                {!props.disableAll && (
                  <div>
                    <Tooltip placement="topLeft" overlayClassName="tooltip-protected" title={"Delete"}>
                      <span
                        onClick={() => {
                          remove(index);
                          // enableClone(typeM);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <DeleteIcon />
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
            </Row>
          </Fragment>
        );
      })}
    </>
  );
};
